import type { SorterResult } from "antd/es/table/interface";
import { type FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import type { SiteGroupList } from "src/app/features/SiteGroup/types/SiteGroupList";
import type { ExtendedColumnsType, ResourceTableProps } from "../../../../../components";
import { GenericTable } from "../../../../../components";
import {
    displayDateTimeValue,
    displayOrgUnitUpdateStatusValue,
} from "../../../../../components/Table/components/PropertyTable/transformations/cellTransformations";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../core/router/types/routerTypes";

export const SiteGroupsTable: FC<ResourceTableProps<SiteGroupList>> = ({ onStateChanged, onRowSelectionChange, datasource, resource }) => {
    const { t } = useTranslation();
    const getColumnsConfig = useCallback(
        (sortInfo: SorterResult<SiteGroupList>): ExtendedColumnsType<SiteGroupList>[] => {
            return [
                {
                    title: t("genericTable.column.id"),
                    key: "id",
                    dataIndex: "id",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "id" ? sortInfo.order : null,
                },
                {
                    title: t("genericTable.column.name"),
                    key: "name",
                    dataIndex: "name",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "name" ? sortInfo.order : null,
                },
                {
                    title: t("siteGroups.column.siteGroupId"),
                    key: "siteGroupId",
                    dataIndex: "siteGroupId",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "siteId" ? sortInfo.order : null,
                },
                {
                    title: t("siteGroups.column.parentUnit"),
                    key: "parentUnitName",
                    dataIndex: "parentUnitName",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "parentUnitName" ? sortInfo.order : null,
                },
                {
                    title: t("genericTable.column.status"),
                    key: "status",
                    dataIndex: "status",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "status" ? sortInfo.order : null,
                    render: (value) => displayOrgUnitUpdateStatusValue(value),
                },
                {
                    title: t("genericTable.column.changed"),
                    dataIndex: "changed",
                    key: "changed",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "changed" ? sortInfo.order : null,
                    render: (value) => displayDateTimeValue(value),
                },
            ];
        },
        [t],
    );

    const navigate = useNavigate();
    const onRowClick = (record: SiteGroupList) => {
        navigate(`${BASE_PATH}/${ResourceName.SITE_GROUPS}/${FORM_ACTION.EDIT}/${record.id}?id=${record.id}`);
    };

    return (
        (datasource && (
            <GenericTable<SiteGroupList>
                getColumnsConfig={getColumnsConfig}
                datasource={datasource}
                name="siteGroups_table"
                onSelectionCallback={onRowSelectionChange}
                onStateChanged={onStateChanged}
                onRow={(record: SiteGroupList) => {
                    return {
                        onClick: () => onRowClick(record),
                    };
                }}
                resource={resource}
            />
        )) ||
        null
    );
};
